// WP-1.2/1.5 (S-09 `/tx`): FilterBar (account/type — a first slice) + DataTable (date,
// party/category, account, source glyph, amount) + footer sum. ＋ Add opens the record
// form; a separate Transfer action + in-transit list covers M9 (no dedicated /transfers
// route — §18.1's sidebar has no such nav item; transfers are a FormSheet off this page,
// per the S-09 spec's own FormSheet list: "quick, income, expense, transfer, refund, adjustment").
import { parseAmountInput, type CurrencyCode } from '@pfos/engine';
import type { AccountResponse, TransactionResponse } from '@pfos/shared';
import { observeElementRect, useVirtualizer } from '@tanstack/react-virtual';
import { ArrowLeftRight, Download, Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Amount } from '../components/Amount';
import {
  AmountField,
  ghostButtonInlineClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { EmptyState } from '../components/EmptyState';
import { TransactionForm } from '../components/TransactionForm';
import { TransferForm } from '../components/TransferForm';
import { useAccounts } from '../lib/accounts';
import { buildTransactionsExport, downloadCsv, downloadXlsx } from '../lib/export';
import { useTags } from '../lib/tags';
import { useTransactions } from '../lib/transactions';
import { useSettleTransfer, useTransfers } from '../lib/transfers';

const SOURCE_GLYPH: Record<string, string> = {
  manual: '✍',
  ai: '✦',
  whatsapp: '🟢',
  import: '⇪',
  recurring: '⟳',
  system: '⚙',
};

function accountName(accounts: AccountResponse[] | undefined, id: string): string {
  return accounts?.find((a) => a.id === id)?.name ?? id;
}

function SettleRow({
  transferId,
  currency,
}: {
  transferId: string;
  currency: CurrencyCode;
}): React.JSX.Element {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const settleMutation = useSettleTransfer(transferId);

  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="flex-1">
        <AmountField
          id={`settle-${transferId}`}
          ariaLabel="Amount received"
          currency={currency}
          value={amount}
          onChange={setAmount}
        />
      </div>
      <button
        className={primaryButtonInlineClass}
        disabled={settleMutation.isPending}
        onClick={() => {
          setError(null);
          const parsed = parseAmountInput(amount, { defaultCurrency: currency });
          if (!parsed.ok) return; // AmountField already shows this inline
          settleMutation.mutate(parsed.money.minorUnits, {
            onError: (e) => {
              setError(e.message);
            },
          });
        }}
      >
        Confirm
      </button>
      {error !== null && <p className="text-sm text-danger">{error}</p>}
    </div>
  );
}

function TransfersSection(): React.JSX.Element | null {
  const { data: accounts } = useAccounts();
  const { data: transfers } = useTransfers();
  const [settlingId, setSettlingId] = useState<string | null>(null);

  if (transfers === undefined || transfers.length === 0) return null;

  return (
    <div className="mb-6 rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">Transfers</p>
      <ul className="space-y-3">
        {transfers.map((t) => {
          const toAccount = (accounts ?? []).find((a) => a.id === t.toAccountId);
          return (
            <li key={t.id} className="text-sm">
              <div className="flex items-center justify-between">
                <span className="text-ink">
                  {accountName(accounts, t.fromAccountId)} → {accountName(accounts, t.toAccountId)}
                </span>
                <Amount currency={t.sentCurrency} minorUnits={t.sentMinor} />
              </div>
              <p className="text-xs text-muted">
                {t.status === 'settled' ? 'Settled' : 'In-Transit'} · {t.initiatedAt.slice(0, 10)}
              </p>
              {/* FX-4: conversion loss (or gain, when negative) once both estimate and actual are known. */}
              {t.conversionLossMinor !== undefined && t.conversionLossMinor !== 0 && (
                <p className="text-xs text-muted">
                  {t.conversionLossMinor > 0 ? 'Conversion loss' : 'Conversion gain'}:{' '}
                  <Amount
                    currency={toAccount?.currency ?? t.sentCurrency}
                    minorUnits={Math.abs(t.conversionLossMinor)}
                  />
                </p>
              )}
              {t.status === 'in_transit' && (
                <>
                  {settlingId === t.id ? (
                    <SettleRow transferId={t.id} currency={toAccount?.currency ?? t.sentCurrency} />
                  ) : (
                    <button
                      className={`${ghostButtonInlineClass} mt-1`}
                      onClick={() => {
                        setSettlingId(t.id);
                      }}
                    >
                      Mark arrived
                    </button>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// S-09 "Export current view (CSV/XLSX)" — a small menu that exports exactly the rows the
// current filter is showing, with the RE-4 valuation-basis footnote baked in by lib/export.
function ExportMenu({
  transactions,
  accounts,
  filterSummary,
}: {
  transactions: TransactionResponse[];
  accounts: AccountResponse[] | undefined;
  filterSummary: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const exportAs = (kind: 'csv' | 'xlsx'): void => {
    const data = buildTransactionsExport(transactions, accounts, filterSummary);
    if (kind === 'csv') downloadCsv('transactions.csv', data);
    else downloadXlsx('transactions.xlsx', 'Transactions', data);
    setOpen(false);
  };
  return (
    <div className="relative">
      <button
        type="button"
        className={ghostButtonInlineClass}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <span className="flex items-center gap-1">
          <Download size={16} /> Export
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 w-32 rounded-[var(--radius-control)] border border-border bg-surface p-1 shadow-md"
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full rounded px-2 py-1.5 text-left text-sm text-ink hover:bg-bg"
            onClick={() => {
              exportAs('csv');
            }}
          >
            CSV
          </button>
          <button
            type="button"
            role="menuitem"
            className="block w-full rounded px-2 py-1.5 text-left text-sm text-ink hover:bg-bg"
            onClick={() => {
              exportAs('xlsx');
            }}
          >
            XLSX
          </button>
        </div>
      )}
    </div>
  );
}

export function TransactionsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [accountId, setAccountId] = useState('');
  const [type, setType] = useState('');
  const [tag, setTag] = useState('');
  const { data: accounts } = useAccounts();
  const { data: tags } = useTags();
  const { data: transactions, isLoading } = useTransactions({
    ...(accountId !== '' ? { accountId } : {}),
    ...(type !== '' ? { type: type as 'expense' | 'income' } : {}),
    ...(tag !== '' ? { tag } : {}),
  });
  const [formOpen, setFormOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  // WP-7.6: the fetch stays unbounded (Dashboard/PartyDetail/ProjectDetail need the full
  // set for aggregates) — only this list's DOM is windowed, via a real-<tr> "padding row"
  // virtualizer so table column layout/semantics survive.
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: transactions?.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 12,
    // jsdom reports 0 for clientHeight/getBoundingClientRect (no real layout), and the
    // default observer applies that 0 immediately on mount — overriding initialRect before
    // it ever helps. Ignoring a reported {0,0} keeps the (nonzero) initialRect in that case,
    // while real browsers (which never report an honest, laid-out 0×0 for this table) still
    // get correctly measured on every resize.
    initialRect: { width: 0, height: 600 },
    observeElementRect: (instance, cb) =>
      observeElementRect(instance, (rect) => {
        if (rect.width === 0 && rect.height === 0) return;
        cb(rect);
      }),
  });

  const filterParts: string[] = [];
  if (accountId !== '') filterParts.push(`account = ${accountName(accounts, accountId)}`);
  if (type !== '') filterParts.push(`type = ${type}`);
  if (tag !== '') filterParts.push(`tag = ${tags?.find((t) => t.id === tag)?.name ?? tag}`);
  const filterSummary = filterParts.length > 0 ? `Filter: ${filterParts.join(', ')}` : '';

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Transactions</h1>
        <div className="flex gap-2">
          {transactions !== undefined && transactions.length > 0 && (
            <ExportMenu
              transactions={transactions}
              accounts={accounts}
              filterSummary={filterSummary}
            />
          )}
          <button
            className={ghostButtonInlineClass}
            onClick={() => {
              setTransferOpen(true);
            }}
          >
            Transfer
          </button>
          <button
            className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
            onClick={() => {
              setFormOpen(true);
            }}
          >
            <Plus size={16} /> Add
          </button>
        </div>
      </div>

      <TransfersSection />

      <div className="mb-4 flex flex-wrap gap-2">
        <select
          aria-label="Filter by account"
          className={`${selectClass} w-auto`}
          value={accountId}
          onChange={(e) => {
            setAccountId(e.target.value);
          }}
        >
          <option value="">All accounts</option>
          {(accounts ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by type"
          className={`${selectClass} w-auto`}
          value={type}
          onChange={(e) => {
            setType(e.target.value);
          }}
        >
          <option value="">All types</option>
          <option value="expense">Expense</option>
          <option value="income">Income</option>
        </select>
        {tags !== undefined && tags.length > 0 && (
          <select
            aria-label="Filter by tag"
            className={`${selectClass} w-auto`}
            value={tag}
            onChange={(e) => {
              setTag(e.target.value);
            }}
          >
            <option value="">All tags</option>
            {tags
              // Archived tags stay filterable (same precedent as the account filter above,
              // which never excludes archived accounts either) — archiving a tag doesn't
              // erase historical taggings, so excluding it here would silently strand
              // already-tagged transactions with no way to find them by that tag again.
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
        )}
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && (transactions === undefined || transactions.length === 0) && (
        <EmptyState
          icon={ArrowLeftRight}
          message="No transactions yet. Add one with ＋ or just message your assistant on WhatsApp."
          action={{
            label: 'Add',
            onClick: () => {
              setFormOpen(true);
            },
          }}
        />
      )}

      {transactions !== undefined && transactions.length > 0 && (
        <div
          ref={parentRef}
          className="overflow-auto rounded-[var(--radius-card)] border border-border"
          style={{ maxHeight: '70vh' }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Category / party</th>
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 text-center font-medium">Src</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const virtualItems = rowVirtualizer.getVirtualItems();
                const topPad = virtualItems[0]?.start ?? 0;
                const bottomPad =
                  rowVirtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0);
                return (
                  <>
                    <tr style={{ height: topPad }}>
                      <td colSpan={5} />
                    </tr>
                    {virtualItems.map((virtualRow) => {
                      const tx = transactions[virtualRow.index];
                      if (tx === undefined) return null;
                      return (
                        <tr
                          key={tx.id}
                          className="cursor-pointer border-b border-border last:border-0 hover:bg-bg"
                          onClick={() => {
                            navigate(`/tx/${tx.id}`);
                          }}
                        >
                          <td className="px-3 py-2 text-ink">{tx.at.slice(0, 10)}</td>
                          <td className="px-3 py-2 text-ink">
                            {tx.category ?? tx.party ?? '—'}
                            {tx.reversed && (
                              <span className="ml-2 text-xs text-muted">(reversed)</span>
                            )}
                            {tx.duplicateOf !== undefined && !tx.reversed && (
                              <span
                                title="Same account, amount, within a day of another — check Data Health"
                                className="ml-2 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning"
                              >
                                Possible duplicate
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-muted">
                            {accountName(accounts, tx.accountId)}
                          </td>
                          <td className="px-3 py-2 text-center" title={tx.source}>
                            {SOURCE_GLYPH[tx.source] ?? tx.source}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Amount
                              currency={tx.currency}
                              minorUnits={tx.type === 'expense' ? -tx.amountMinor : tx.amountMinor}
                              className={tx.type === 'expense' ? 'text-danger' : 'text-ink'}
                            />
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{ height: bottomPad }}>
                      <td colSpan={5} />
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      )}

      <TransactionForm open={formOpen} onOpenChange={setFormOpen} />
      <TransferForm open={transferOpen} onOpenChange={setTransferOpen} />
    </div>
  );
}
